import { useEffect, useState, useCallback, useRef } from 'react';
import { listProducts, getCode, setCode, clearCode, lockMessage } from '../api/lionjob';
import LionjobPager from '../components/LionjobPager';

// Màn KHO sản phẩm (Lionjob · Scraped Products). Chỉ Giám đốc + mã code (dùng chung với Listing Task).
export default function ScrapedProductsPage({ onOpenListing }) {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPageN] = useState(1);
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [msg, setMsg] = useState('');
  const [extReady, setExtReady] = useState(false); // extension 🦁 đã cài & nhận diện được
  const [scanning, setScanning] = useState(false);
  // Nguồn hàng: 'crawl' = mình tự cào thẳng store, 'matrixty' = lấy lại từ kho matrixty.
  const [origin, setOrigin] = useState('all');
  const [counts, setCounts] = useState({ crawl: 0, matrixty: 0, all: 0 });
  const [crawlInput, setCrawlInput] = useState('');
  const [crawling, setCrawling] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listProducts({ page, limit: 20, q: q || undefined, origin: origin === 'all' ? undefined : origin });
      setRows(data.rows); setMeta({ page: data.page, totalPages: data.totalPages, total: data.total });
      if (data.originCounts) setCounts(data.originCounts);
      setUnlocked(true);
    } catch (e) {
      const m = e.response?.data?.message || e.message || 'Không kết nối được backend';
      if (e.response?.status === 403) { clearCode(); setUnlocked(false); }
      setLockErr(lockMessage(e)); setMsg('Lỗi: ' + m);
    }
  }, [page, q, origin]);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);
  useEffect(() => { if (getCode()) load(); /* eslint-disable-next-line */ }, []);

  // Cầu nối extension 🦁: nhận diện + nhận kết quả quét MÀN KHO (kind='product'), rồi reload.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== 'lionjob-ext') return;
      if (ev.data.type === 'ready') setExtReady(true);
      if (ev.data.type === 'scan-result') {
        setScanning(false);
        const r = ev.data.resp;
        if (r && r.ok) {
          setMsg(`✓ Quét ${r.scanned ?? r.received} sản phẩm · mới ${r.inserted}, cập nhật ${r.updated}`);
          loadRef.current && loadRef.current();
        } else {
          setMsg('✗ ' + ((r && r.error) || 'Quét lỗi'));
        }
      }
      if (ev.data.type === 'crawl-url-result') {
        setCrawling(false);
        const r = ev.data.resp;
        if (r && r.ok) {
          setMsg(`✓ Cào ${r.found} sản phẩm (${r.platform}) · mới ${r.inserted}, cập nhật ${r.updated}`
            + (r.flaggedCount ? ` · ⚠ ${r.flaggedCount} bị gắn cờ bản quyền/không POD` : ''));
          loadRef.current && loadRef.current();
        } else {
          setMsg('✗ ' + ((r && r.error) || 'Cào lỗi'));
        }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'lionjob-page', type: 'ping' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // ĐƯỜNG ĐỘC LẬP: dán link store/collection -> extension mở tab ngầm, cào, đẩy về, đóng tab.
  // Không đụng gì tới matrixty.
  function crawlUrl() {
    const u = crawlInput.trim();
    if (!u) { setMsg('Dán đường dẫn trang store (vd https://shop.com/collections/all).'); return; }
    if (!/^https?:\/\//i.test(u)) { setMsg('Đường dẫn phải bắt đầu bằng http:// hoặc https://'); return; }
    if (!extReady) { setMsg('Chưa thấy extension 🦁 — cài extension rồi tải lại trang.'); return; }
    setCrawling(true);
    setMsg('Đang mở trang và cào… (có thể mất tới 90 giây với shop lớn)');
    window.postMessage({ source: 'lionjob-page', type: 'crawl-url', url: u }, '*');
    setTimeout(() => setCrawling((s) => { if (s) setMsg('Cào quá lâu / không phản hồi.'); return false; }), 95000);
  }

  function scanFromMatrixty() {
    if (!extReady) { setMsg('Chưa thấy extension 🦁 — cài extension rồi mở tab matrixty.com.'); return; }
    setScanning(true);
    setMsg('Đang quét Scraped Products từ Matrixty…');
    window.postMessage({ source: 'lionjob-page', type: 'scan', kind: 'product' }, '*');
    setTimeout(() => setScanning((s) => { if (s) setMsg('Quét quá lâu / không phản hồi — kiểm tra tab matrixty.com.'); return false; }), 45000);
  }

  async function unlock(e) {
    e?.preventDefault();
    const c = codeInput.trim(); if (!c) { setLockErr('Nhập mã code'); return; }
    setLockErr(''); setCode(c); setUnlocking(true);
    try { await load(); } finally { setUnlocking(false); }
  }
  function copyText(t) { navigator.clipboard.writeText(String(t || '')); setMsg('✓ Đã copy: ' + t); }
  function goListing() { onOpenListing && onOpenListing(); }

  const active = rows.find((r) => r.id === activeId) || null;

  if (!unlocked) {
    return (
      <div className="mx-root"><style>{CSS}</style>
        <div className="mx-lock"><div className="mx-lockCard">
          <div className="mx-lockIco">🦁</div><h2>Scraped Products</h2>
          <p className="mx-lockSub">Khu vực chỉ dành cho Giám đốc. Nhập mã code để mở.</p>
          <form onSubmit={unlock}>
            <input className="mx-lockInput" type="password" autoFocus placeholder="Mã code" disabled={unlocking} value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
            <button className="mx-btn primary" type="submit" disabled={unlocking} style={{ width: '100%', marginTop: 10 }}>
              {unlocking ? '⏳ Đang kết nối…' : 'Mở khóa'}</button>
          </form>
          {lockErr && <div className="mx-lockErr">⚠ {lockErr}</div>}
        </div></div>
      </div>
    );
  }

  const badge = (r) => (
    <>
      <span className={'mx-badge ' + r.seoStatus}>{r.seoStatus}</span>
      {r.hasSeo ? <span className="mx-badge doing">có SEO</span> : null}
    </>
  );

  return (
    <div className="mx-root"><style>{CSS}</style>
      <div className="mx-head">
        <div>
          <h2>Scraped Products <span className="mx-tag">kho</span></h2>
          <div className="mx-sub">{msg || `${meta.total} sản phẩm · trang ${meta.page}/${meta.totalPages}`}</div>
        </div>
        <div className="mx-spacer" />
        <input className="mx-search" placeholder="Tìm theo tên…" value={q} onChange={(e) => { setQ(e.target.value); setPageN(1); }} />
        <button className="mx-btn" disabled={page <= 1} onClick={() => setPageN((p) => p - 1)}>‹</button>
        <button className="mx-btn" disabled={page >= meta.totalPages} onClick={() => setPageN((p) => p + 1)}>›</button>
      </div>

      {/* Hai đường lấy hàng, tách bạch để biết cái nào còn lệ thuộc matrixty. */}
      <div className="mx-sources">
        <div className="mx-srcBox own">
          <div className="mx-srcHead">
            <b>🕷 Tự cào</b>
            <span className="mx-srcNote">Mở thẳng trang store rồi lấy — không cần matrixty</span>
          </div>
          <div className="mx-srcRow">
            <input className="mx-urlInput" placeholder="https://shop-doi-thu.com/collections/all"
              value={crawlInput} onChange={(e) => setCrawlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') crawlUrl(); }} disabled={crawling} />
            <button className="mx-btn primary" onClick={crawlUrl} disabled={crawling || !extReady}
              title={extReady ? 'Mở tab ngầm, cào sản phẩm rồi đóng lại' : 'Cần cài extension 🦁'}>
              {crawling ? '⏳ Đang cào…' : 'Cào'}
            </button>
          </div>
          <div className="mx-srcHint">
            Shopify lấy qua <code>products.json</code> (đủ giá · tags · mô tả · SKU) · Etsy/Amazon đọc lưới.
            Đang đứng sẵn trên trang store thì dùng popup extension → <b>🕷 Cào sản phẩm trang đang mở</b>.
          </div>
        </div>

        <div className="mx-srcBox dep">
          <div className="mx-srcHead">
            <b>🦁 Lấy từ matrixty</b>
            <span className="mx-srcNote">Phụ thuộc kho của họ — cần tab matrixty.com đã đăng nhập</span>
          </div>
          <div className="mx-srcRow">
            <button className="mx-btn" onClick={scanFromMatrixty} disabled={scanning || !extReady}
              title={extReady ? 'Quét KHO sản phẩm từ matrixty.com (/scraped-products)' : 'Cần cài extension 🦁'}>
              {scanning ? '⏳ Đang quét…' : 'Quét Scraped Products'}
            </button>
          </div>
          <div className="mx-srcHint">Dữ liệu matrixty cũng do extension của họ cào ra theo đúng cách trên.</div>
        </div>
      </div>

      <div className="mx-filters">
        {[
          { k: 'all', t: 'Tất cả', n: counts.all },
          { k: 'crawl', t: '🕷 Tự cào', n: counts.crawl },
          { k: 'matrixty', t: '🦁 Từ matrixty', n: counts.matrixty },
        ].map((f) => (
          <button key={f.k} className={'mx-pill' + (origin === f.k ? ' on' : '')}
            onClick={() => { setOrigin(f.k); setPageN(1); }}>{f.t} <span className="c">{f.n}</span></button>
        ))}
      </div>

      <div className="mx-wrap">
        <aside className="mx-list">
          {rows.length === 0 && <div className="mx-empty">Không có sản phẩm</div>}
          {rows.map((r) => (
            <div key={r.id} className={'mx-item' + (r.id === activeId ? ' active' : '')} onClick={() => setActiveId(r.id)}>
              <div className="mx-itrow">
                {r.productImage ? <img className="mx-thumb" src={r.productImage} alt="" /> : <div className="mx-thumb" />}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mx-it">{r.productTitle || '(chưa có title)'}</div>
                  <div className="mx-im">
                    <span className={'mx-orig ' + r.origin} title={r.origin === 'crawl' ? 'Mình tự cào' : 'Lấy về từ matrixty'}>
                      {r.origin === 'crawl' ? '🕷' : '🦁'}</span>
                    {r.variant} · {r.niche} {badge(r)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </aside>

        <main className="mx-detail">
          {!active && <div className="mx-empty">Chọn một sản phẩm để xem chi tiết.</div>}
          {active && (
            <>
              <div className="mx-src">
                {active.productImage && <img src={active.productImage} alt="" />}
                <div>
                  <div className="mx-srcTitle">{active.productTitle || '(chưa có title nguồn)'}</div>
                  <div className="mx-srcMeta">{active.website} · {active.variant} · {active.niche}{active.shopName ? ' · shop ' + active.shopName : ''}</div>
                  <div className="mx-sku">SKU: <b>{active.sku}</b>
                    <button className="mx-btn sm" onClick={() => copyText(active.sku)}>⧉ copy</button></div>
                </div>
              </div>

              <div className="mx-actions">
                <span className={'mx-badge ' + active.seoStatus}>{active.seoStatus}</span>
                {active.hasSeo ? <span className="mx-badge doing">đã có SEO</span> : <span className="mx-badge pending">chưa SEO</span>}
                <span className={'mx-badge ' + (active.origin === 'crawl' ? 'done' : '')}>
                  {active.origin === 'crawl' ? '🕷 mình tự cào' : '🦁 lấy từ matrixty'}</span>
                <span className="mx-badge">nguồn: {active.sourceStatus}</span>
                {active.productUrl && <a className="mx-btn sm" href={active.productUrl} target="_blank" rel="noreferrer">↗ Mở nguồn (Drive)</a>}
                {active.publishLink && <a className="mx-btn sm ok" href={active.publishLink} target="_blank" rel="noreferrer">↗ Listing Etsy</a>}
                <button className="mx-btn primary" onClick={goListing}>→ Làm SEO ở Listing Task</button>
              </div>

              {active.note && (
                <div className="mx-field"><label>Ghi chú agent (rationale)</label>
                  <div className="mx-note">{active.note}</div></div>
              )}
            </>
          )}
        </main>
      </div>

      <LionjobPager page={meta.page} totalPages={meta.totalPages} onGo={(p) => setPageN(p)} />
    </div>
  );
}

const CSS = `
.mx-root{color:var(--text)}
.mx-head{display:flex;gap:10px;align-items:center;margin-bottom:14px}
.mx-head h2{margin:0;font-size:18px}
.mx-tag{font-size:11px;color:var(--accent);border:1px solid var(--accent);border-radius:6px;padding:1px 6px;margin-left:6px;vertical-align:middle}
.mx-sub{color:var(--muted);font-size:12px;margin-top:2px}
.mx-spacer{flex:1}
.mx-search{background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:7px 11px;font-size:13px;width:200px}
.mx-btn{background:var(--panel);color:var(--text);border:1px solid var(--border);padding:7px 12px;border-radius:10px;cursor:pointer;font-size:13px;text-decoration:none;display:inline-flex;align-items:center;gap:4px}
.mx-btn.sm{padding:4px 10px;font-size:12px}
.mx-btn:hover:not(:disabled){border-color:var(--accent)}
.mx-btn:disabled{opacity:.45;cursor:not-allowed}
.mx-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
.mx-btn.ok{border-color:var(--ok);color:var(--ok)}
.mx-sources{display:grid;grid-template-columns:1.6fr 1fr;gap:12px;margin-bottom:12px}
.mx-srcBox{border:1px solid var(--border);border-radius:var(--radius);padding:11px 13px;background:var(--panel)}
.mx-srcBox.own{border-color:var(--ok)}
.mx-srcBox.dep{opacity:.85}
.mx-srcHead{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:8px}
.mx-srcHead b{font-size:13px}
.mx-srcNote{color:var(--muted);font-size:11.5px}
.mx-srcRow{display:flex;gap:8px}
.mx-urlInput{flex:1;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:7px 11px;font-size:13px;min-width:0}
.mx-srcHint{color:var(--muted);font-size:11px;margin-top:7px;line-height:1.5}
.mx-srcHint code{background:var(--input);padding:0 4px;border-radius:4px}
.mx-filters{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap}
.mx-pill{border:1px solid var(--border);background:var(--panel);color:var(--muted);padding:5px 13px;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer}
.mx-pill.on{background:var(--accent);color:#fff;border-color:var(--accent)}
.mx-pill .c{opacity:.7;margin-left:3px}
.mx-orig{font-size:11px;flex:none}
.mx-wrap{display:grid;grid-template-columns:340px 1fr;gap:14px;min-height:60vh}
.mx-list{border:1px solid var(--border);border-radius:var(--radius);overflow:auto;max-height:74vh;background:var(--panel)}
.mx-item{padding:9px 12px;border-bottom:1px solid var(--line);cursor:pointer}
.mx-item:hover{background:var(--input)}
.mx-item.active{background:var(--input);border-left:2px solid var(--accent)}
.mx-itrow{display:flex;gap:10px;align-items:center}
.mx-thumb{width:40px;height:40px;border-radius:8px;object-fit:cover;border:1px solid var(--border);background:var(--input);flex:none}
.mx-it{font-weight:600;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mx-im{color:var(--muted);font-size:11px;margin-top:2px;display:flex;gap:6px;align-items:center}
.mx-badge{display:inline-block;padding:1px 7px;border-radius:20px;font-size:11px;border:1px solid var(--border)}
.mx-badge.pending{color:var(--warn);border-color:var(--warn)}
.mx-badge.doing{color:var(--info);border-color:var(--info)}
.mx-badge.done{color:var(--ok);border-color:var(--ok)}
.mx-detail{border:1px solid var(--border);border-radius:var(--radius);padding:16px 18px;background:var(--panel);overflow:auto;max-height:74vh}
.mx-empty{padding:50px;text-align:center;color:var(--muted)}
.mx-src{display:flex;gap:12px;align-items:flex-start;margin-bottom:12px}
.mx-src img{width:72px;height:72px;border-radius:8px;object-fit:cover;border:1px solid var(--border)}
.mx-srcTitle{font-weight:600}
.mx-srcMeta{color:var(--muted);font-size:12px;margin-top:2px}
.mx-sku{font-size:12px;color:var(--muted);margin-top:4px;font-family:var(--font-mono);display:flex;align-items:center;gap:8px}
.mx-sku b{color:var(--text);letter-spacing:.5px}
.mx-actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:12px 0}
.mx-field label{display:block;color:var(--muted);font-size:12px;text-transform:uppercase;letter-spacing:.5px;margin:12px 0 4px}
.mx-note{background:var(--input);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:12px;white-space:pre-wrap;font-family:var(--font-mono);max-height:320px;overflow:auto}
.mx-lock{display:flex;justify-content:center;padding:60px 20px}
.mx-lockCard{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:28px 26px;width:340px;text-align:center}
.mx-lockIco{font-size:38px;line-height:1}.mx-lockCard h2{margin:8px 0 4px}
.mx-lockSub{color:var(--muted);font-size:13px;margin:0 0 16px}
.mx-lockInput{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:14px;text-align:center;letter-spacing:2px}
.mx-lockErr{color:var(--danger);font-size:12px;margin-top:10px}
`;
